import { tracked } from '@glimmer/tracking';
import cloneDeep from 'lodash.clonedeep';

import { GET_CDP_NUMERIC_VALUE, GET_CDP_BOOLEAN_VALUE } from '../../utils/formulas';
import Utils from '../../utils/utils';
import Constants from '../../utils/constants';

export default class Resistor {
  @tracked hasInternalResistors = null;
  @tracked internalResistors = null;
  @tracked internalTolerance = null;
  @tracked internalTempco = null;
  @tracked res1 = null; // Can appear in the reset list and thus in resetProperty
  @tracked res2 = null; // Can appear in the reset list and thus in resetProperty
  @tracked tempco = null; // Can appear in the reset list and thus in resetProperty
  @tracked tolerance = null; // Can appear in the reset list and thus in resetProperty
  @tracked amplifierGainMetadata;
  @tracked id;
  @tracked isShown;
  @tracked isCurrent;
  @tracked isDisabled;

  constructor(resistor) {
    if (resistor) {
      this.name = resistor.name;
      this.appName = resistor.appName;
      this.propertyName = resistor.propertyName;
      this.fileName = resistor.fileName;

      this.article = resistor.article;
      this.amplifierGainMetadata = resistor.amplifierGainMetadata ?? { default: null, fn: () => null };
      this.cdpParameters = resistor.cdpParameters;
      this.defaults = resistor.defaults ?? {};
      this.findResistorNetworkPart = resistor.findResistorNetworkPart ?? true;
      this.hasInternalParamsKey = resistor.hasInternalParamsKey ?? false;
      this.hideRes1 = resistor.hideRes1 ?? false;
      this.id = resistor.id ?? '';
      this.internalParamKey = resistor.internalParamKey ?? null;
      this.isAlwaysInternal = resistor.isAlwaysInternal ?? false;
      this.isDisabled = resistor.isDisabled ?? false;
      this.isShown = resistor.isShown ?? true;
      this.isPositive = resistor.isPositive ?? true;
      this.isInPrimaryVoltagePath = resistor.isInPrimaryVoltagePath ?? false;
      this.isInSecondaryVoltagePath = resistor.isInSecondaryVoltagePath ?? false;
      this.isResistorDisabled = resistor.isResistorDisabled ?? false;
      this.requiresGainValidation = resistor.requiresGainValidation ?? true;
      this.res1Type = resistor.res1Type ?? 'r1';
      this.res2Type = resistor.res2Type ?? 'r2';
      this.reset = resistor.reset ?? {};
      this.suppressTempCo = resistor.suppressTempCo ?? true;
      this.tempcoParamKey = resistor.tempcoParamKey ?? null;
      this.toleranceParamKey = resistor.toleranceParamKey ?? null;
      this.useDefaultResistorLabel = resistor.useDefaultResistorLabel ?? true;
    }
  }

  clone() {
    let result = new Resistor(cloneDeep(this));
    // Since tracked data isn't owned and hence doesn't clone, copy by hand...
    result.id = this.id;
    result.hasInternalResistors = this.hasInternalResistors;
    result.internalResistors = this.internalResistors;
    result.internalTolerance = this.internalTolerance;
    result.internalTempco = this.internalTempco;
    result.res1 = this.res1;
    result.res2 = this.res2;
    result.tempco = this.tempco;
    result.tolerance = this.tolerance;
    result.amplifierGainMetadata = cloneDeep(this.amplifierGainMetadata);
    result.isShown = this.isShown;
    result.isCurrent = this.isCurrent;
    result.isDisabled = this.isDisabled;
    return result;
  }

  get amplifierGain() {
    if (!this.values) return null;
    return this.getAmplifierGain(this.values.res1, this.values.res2);
  }

  get requiresValidation() {
    return this.isCurrent && this.isShown && !this.isDisabled;
  }

  get useInUrl() {
    return !this.isDisabled && this.isShown && this.isCurrent;
  }

  get isInverting() {
    const invertingResistors = [Constants.RESISTORS.INVERTING];
    return invertingResistors.includes(this.id);
  }

  get isNonInverting() {
    return this.id === Constants.RESISTORS.NON_INVERTING;
  }

  get isOutput() {
    return this.id === Constants.RESISTORS.OUTPUT;
  }

  get values() {
    let result = {};
    if (this.hasInternalResistors && this.isCurrent) {
      result.res1 = Utils.numericOrDefault(this.internalResistors, this.defaults.res1);
      result.res2 = Utils.numericOrDefault(this.internalResistors, this.defaults.res2);
      result.tolerance = Utils.numericOrDefault(this.internalTolerance, this.defaults.tolerance);
      result.tempco = Utils.numericOrDefault(this.internalTempco, this.defaults.tempco);
    } else {
      result.res1 = Utils.numericOrDefault(this.res1, this.defaults.res1);
      result.res2 = Utils.numericOrDefault(this.res2, this.defaults.res2);
      result.tolerance = Utils.numericOrDefault(this.tolerance, this.defaults.tolerance);
      result.tempco = Utils.numericOrDefault(this.tempco, this.defaults.tempco);
    }

    return result;
  }

  get canCalculateAmplifierGain() {
    return (
      this.amplifierGainMetadata &&
      this.amplifierGainMetadata.fn &&
      Utils.isNumeric(this.values.res1) &&
      Utils.isNumeric(this.values.res2)
    );
  }

  get hasInternalCapabilities() {
    return this.isAlwaysInternal || this.hasInternalParamsKey;
  }

  getDefaultAmplifierGain() {
    return this.amplifierGainMetadata && Utils.isNumeric(this.amplifierGainMetadata.default)
      ? this.amplifierGainMetadata.default
      : null;
  }

  getAmplifierGain(res1, res2) {
    if (this.canCalculateAmplifierGain) return this.amplifierGainMetadata.fn(res1, res2);
    else return this.getDefaultAmplifierGain();
  }

  resetHasInternalParams(selectedDac) {
    let isInternal = this.isAlwaysInternal;
    if (!isInternal)
      isInternal = GET_CDP_BOOLEAN_VALUE(this.hasInternalParamsKey, selectedDac, this.cdpParameters).value;
    this.hasInternalResistors = isInternal;
  }

  resetInternalResistors(selectedDac) {
    if (this.internalParamKey && selectedDac && this.internalParamKey in selectedDac) {
      const internalResistors = GET_CDP_NUMERIC_VALUE(this.internalParamKey, selectedDac, this.cdpParameters),
        internalTolerance = GET_CDP_NUMERIC_VALUE(this.toleranceParamKey, selectedDac, this.cdpParameters),
        internalTempco = GET_CDP_NUMERIC_VALUE(this.tempcoParamKey, selectedDac, this.cdpParameters);

      this.internalResistors = internalResistors.value;
      this.internalTolerance = internalTolerance.value;
      this.internalTempco = internalTempco.value;
    } else {
      this.internalResistors = null;
      this.internalTolerance = null;
      this.internalTempco = null;
    }
  }

  loadRsetFromPst(selectedDac) {
    this.res2 = GET_CDP_NUMERIC_VALUE(Constants.CDP.PARAMETERS.RSET_RESISTANCE, selectedDac).value;
    this.reset.res2 = GET_CDP_NUMERIC_VALUE(Constants.CDP.PARAMETERS.RSET_RESISTANCE, selectedDac).value;
  }

  // In theory: resetProperty could be called with any resistor property, and resistor.reset could contain any property.
  // In practice today: resetProperty is called with isCurrent, isDisabled, and the four reset properties: res1, res2, tempco, and tolerance
  // If it is used with other properties, make sure they're tracked.
  // N.B: This reliance of what to track on current behavior continuing is a maintenance vulterability in this class right now.
  resetProperty(key, value) {
    this[key] = value;
  }
}
